import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Crown, Radio } from "lucide-react";

const fmtLabel = (f) => (f === "league" ? "League" : "Tournament");

function ActiveList({ items, testid }) {
  if (!items?.length) return <p className="text-xs text-zinc-600 uppercase tracking-widest">No active tournaments right now.</p>;
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((t) => (
        <div key={t.tournament_id} className="flex items-center gap-3 border border-white/10 bg-black/30 px-3 py-2.5" data-testid={`active-tournament-${t.short_code || t.tournament_id}`}>
          <span className="relative flex h-2 w-2 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-70" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
          <span className="font-display tracking-wider uppercase text-sm truncate flex-1">{t.name}</span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 border border-zinc-800 px-1.5 py-0.5">{fmtLabel(t.format)}</span>
        </div>
      ))}
    </div>
  );
}

function RollOfHonour({ items, testid }) {
  return (
    <div data-testid={testid}>
      {!items?.length ? (
        <div className="border border-dashed border-white/10 px-4 py-5 text-center">
          <Trophy size={20} className="mx-auto text-zinc-700 mb-2" />
          <p className="text-xs text-zinc-500 uppercase tracking-widest">Roll of honour awaits its first champion</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((t) => (
            <div key={t.tournament_id} className="flex items-center gap-3 border-l-2 border-yellow-500/70 bg-gradient-to-r from-yellow-500/10 to-transparent px-3 py-2.5" data-testid={`honour-${t.short_code || t.tournament_id}`}>
              <Crown size={18} className="text-yellow-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">{t.name}</div>
                <div className="font-display tracking-wider uppercase text-sm truncate">{t.champion || "—"}</div>
              </div>
              <Trophy size={14} className="text-yellow-500/60 flex-shrink-0" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [pub, setPub] = useState({ active: [], past: [] });

  useEffect(() => {
    api.get("/public/tournaments").then(({ data }) => setPub(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, pw);
      nav(loc.state?.from || "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 grid-bg">
      {/* LEFT — brand + showcase */}
      <div className="hidden lg:flex flex-col p-12 bg-gradient-to-br from-zinc-950 via-zinc-900 to-black border-r border-white/10 relative overflow-y-auto">
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: "url(https://images.unsplash.com/photo-1638573615178-6f2950746614?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400)",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-primary-accent flex items-center justify-center"><Trophy size={22} /></div>
            <div className="font-display text-2xl tracking-wider">JINXING SPORTS</div>
          </div>
          <h1 className="font-display text-5xl xl:text-6xl leading-[0.9] uppercase">
            Predict.<br/>Survive.<br/><span className="text-primary-accent">Conquer.</span>
          </h1>
          <p className="mt-5 text-zinc-400 max-w-md leading-relaxed text-sm">
            Five competitions. One tournament. Endless bragging rights.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-8 max-w-md">
            <div>
              <div className="label-tag mb-3 flex items-center gap-2"><Radio size={12} className="text-green-400" /> Active Tournaments</div>
              <ActiveList items={pub.active} testid="login-active-tournaments" />
            </div>
            <div>
              <div className="label-tag mb-3 flex items-center gap-2"><Crown size={12} className="text-yellow-400" /> Roll of Honour</div>
              <RollOfHonour items={pub.past} testid="login-roll-of-honour" />
            </div>
          </div>
        </div>
        <div className="relative mt-10 pt-6 text-xs text-zinc-600 uppercase tracking-widest">
          Modular · Multi-Tournament · Built for Banter
        </div>
      </div>

      {/* RIGHT — auth */}
      <div className="flex flex-col justify-center p-8 md:p-16">
        <div className="max-w-md w-full mx-auto">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 bg-primary-accent flex items-center justify-center"><Trophy size={18} /></div>
            <div className="font-display text-xl tracking-wider">JINXING SPORTS</div>
          </div>

          <div className="label-tag mb-2">Sign In</div>
          <h2 className="font-display text-4xl uppercase mb-8">Welcome back</h2>

          <button data-testid="google-signin-btn" onClick={googleSignIn}
            className="w-full surface-card py-3 px-4 mb-4 hover:border-white/30 transition-colors flex items-center justify-center gap-3">
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-sm uppercase tracking-widest">Continue with Google</span>
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs uppercase tracking-widest text-zinc-500">or email</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-tag block mb-2">Email</label>
              <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="admin@league.com"
                className="w-full bg-zinc-950 border border-zinc-800 px-4 py-3 focus:border-primary-accent focus:outline-none text-base"/>
            </div>
            <div>
              <label className="label-tag block mb-2">Password</label>
              <input data-testid="login-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required
                placeholder="••••••••"
                className="w-full bg-zinc-950 border border-zinc-800 px-4 py-3 focus:border-primary-accent focus:outline-none text-base"/>
            </div>
            <button data-testid="login-submit-btn" type="submit" disabled={busy}
              className="w-full bg-primary-accent hover:bg-[var(--primary-hover)] text-white font-display tracking-widest py-3 text-lg uppercase disabled:opacity-50">
              {busy ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <p className="mt-6 text-sm text-zinc-400 text-center">
            New here?{" "}
            <Link to="/register" className="text-primary-accent uppercase tracking-widest text-xs" data-testid="signup-link">Create account</Link>
          </p>
          <p className="mt-4 text-xs text-zinc-600 text-center">
            Demo: admin@league.com / admin123 · player1@league.com / player123
          </p>

          {/* Mobile showcase */}
          <div className="lg:hidden mt-10 pt-8 border-t border-white/10 space-y-8">
            <div>
              <div className="label-tag mb-3 flex items-center gap-2"><Radio size={12} className="text-green-400" /> Active Tournaments</div>
              <ActiveList items={pub.active} testid="login-active-tournaments-mobile" />
            </div>
            <div>
              <div className="label-tag mb-3 flex items-center gap-2"><Crown size={12} className="text-yellow-400" /> Roll of Honour</div>
              <RollOfHonour items={pub.past} testid="login-roll-of-honour-mobile" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
